import { normalizeDatesInObject } from './dateNormalization';

/** Stable snapshot for dirty detection (save vs reset guard) */
export function serializeDealState(state) {
    if (!state || typeof state !== 'object') return '';
    try {
        const n = normalizeDatesInObject(JSON.parse(JSON.stringify(state)));
        return JSON.stringify(n);
    } catch {
        return '';
    }
}

function mongoIdToComparableString(raw) {
    if (raw == null) return '';
    if (typeof raw === 'object' && raw.$oid != null) return String(raw.$oid);
    return String(raw);
}

/**
 * Align `_id` vs `id` on the deal object so compare vs persisted snapshot matches the update payload shape.
 */
function normalizeDealForCompare(obj) {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const o = JSON.parse(JSON.stringify(obj));
    if (o._id != null) {
        o.id = mongoIdToComparableString(o._id);
        delete o._id;
    }
    return normalizeDatesInObject(o);
}

function snapshotPieceEqual(a, b) {
    try {
        const na = normalizeDatesInObject(JSON.parse(JSON.stringify(a === undefined ? null : a)));
        const nb = normalizeDatesInObject(JSON.parse(JSON.stringify(b === undefined ? null : b)));
        return JSON.stringify(na) === JSON.stringify(nb);
    } catch {
        return a === b;
    }
}

/**
 * Top-level fields that differ from `persistedSerialized` (last load / successful save).
 * Sends `null` for keys removed from the form vs snapshot. Falls back to full normalized payload if no baseline.
 */
export function getChangedFieldsVsPersisted(current, persistedSerialized) {
    if (!current || typeof current !== 'object') return current;
    if (!persistedSerialized || typeof persistedSerialized !== 'string') {
        return { ...normalizeDealForCompare(current) };
    }
    let baseline;
    try {
        baseline = JSON.parse(persistedSerialized);
    } catch {
        return { ...normalizeDealForCompare(current) };
    }
    const cur = normalizeDealForCompare(current);
    const base = normalizeDealForCompare(baseline);
    const changed = {};
    const keys = new Set([...Object.keys(cur), ...Object.keys(base)]);
    for (const key of keys) {
        if (key === '_id' || key === 'id') continue;
        const inCur = Object.prototype.hasOwnProperty.call(cur, key);
        const inBase = Object.prototype.hasOwnProperty.call(base, key);
        if (!inCur && inBase) {
            changed[key] = null;
            continue;
        }
        if (!inCur && !inBase) continue;
        const curVal = inCur ? cur[key] : undefined;
        const baseVal = inBase ? base[key] : undefined;
        if (!snapshotPieceEqual(curVal, baseVal)) {
            changed[key] = curVal;
        }
    }
    return changed;
}

/** Document is addressed by URL (`opportunityId`); do not send `_id` / `id` in the body. */
export function omitDocumentIdentityFields(obj) {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const { _id, id, ...rest } = obj;
    return rest;
}
